import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import useAdminPanel from '../hooks/useAdminPanel';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import TierAccessMatrix from '../components/TierAccessMatrix';
import AuditLog from '../components/AuditLog';
import CompliancePanel from '../components/admin/CompliancePanel';
import LegalAdminEditor from '../components/LegalAdminEditor';
import StripePayoutsPanel from '../components/admin/StripePayoutsPanel';
import CommissionMatrixEditor from '../components/CommissionMatrixEditor';
import ReconciliationInbox from '../components/ReconciliationInbox';
import CustomTiersTab from '../components/admin/CustomTiersTab';
import YouTubeApprovalTab from '../components/admin/YouTubeApprovalTab';
import AdminTabNav from '../components/admin/AdminTabNav';
import AdvertisersTab from '../components/admin/AdvertisersTab';
import CommissionDashboardTab from '../components/admin/CommissionDashboardTab';
import ContentTab from '../components/admin/ContentTab';
import OnboardingSettingsCard from '../components/admin/OnboardingSettingsCard';
import OnboardingAnalyticsCard from '../components/admin/OnboardingAnalyticsCard';
import NudgeEmailCard from '../components/admin/NudgeEmailCard';
import DownlineTab from '../components/admin/DownlineTab';
import EdirectoryTab from '../components/admin/EdirectoryTab';
import FeaturedMembersTab from '../components/admin/FeaturedMembersTab';
import FounderApprovalsTab from '../components/admin/FounderApprovalsTab';
import FounderMembershipTab from '../components/admin/FounderMembershipTab';
import PotTab from '../components/admin/PotTab';
import PromoTab from '../components/admin/PromoTab';
import SalesTab from '../components/admin/SalesTab';
import SchedulerAdminTab from '../components/admin/SchedulerAdminTab';
import SubscriptionsTab from '../components/admin/SubscriptionsTab';
import TierFeaturesTab from '../components/admin/TierFeaturesTab';
import UsersTab from '../components/admin/UsersTab';
import VideosTab from '../components/admin/VideosTab';
import AIAssistant from '../components/AIAssistant';
import AdminReportsTab from '../components/admin/AdminReportsTab';
import AdminVerifications from '../components/admin/AdminVerifications';
import StripeReconcileTab from '../components/admin/StripeReconcileTab';
import EmailTestCard from '../components/admin/EmailTestCard';
import AppShell from '../components/AppShell';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function AdminPanel() {
  const {
    user, loading, activeTab, setActiveTab,
    pendingFounders, pendingVideos, pendingLibraryVideos,
    promoToDelete, setPromoToDelete, deletePromoCode,
    panel,
  } = useAdminPanel();
  const { getAuthHeader, fetchAllData } = panel;
  // Inline payouts-admin block uses these directly — pulled from the bag
  // for readability rather than re-prefixing every reference with `panel.`.
  const {
    payoutHistory,
    customTiers, showTierForm, setShowTierForm, newTier, setNewTier,
    tierFeaturesInput, setTierFeaturesInput,
    handleCreateTier, handleDeleteTier,
    handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview,
  } = panel;
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading admin panel…</p>
        </div>
      </div>
    );
  }
  if (!user || !user.is_admin) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-muted-foreground">You are not authorised to view this page.</p></div>;
  }

  return (
    <AppShell active="admin" title="Admin Panel">
      {/* Cinematic Millionaire Pot widget row — MasterClass/GoHighLevel admin hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-testid="admin-hero">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="md:col-span-2 rounded-2xl bg-gradient-to-br from-brand-gold/20 to-transparent border border-brand-gold/30 p-6" data-testid="millionaire-pot-widget">
            <div className="flex items-center gap-2 text-brand-gold text-xs uppercase tracking-widest mb-2">
              <span>◆</span> Millionaire Pot
            </div>
            <p className="font-playfair text-4xl font-semibold text-white">
              ${(panel.pot?.total_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Goal ${(panel.pot?.goal_amount ?? 1000000).toLocaleString()} · {panel.pot?.goal_amount ? Math.round((panel.pot.total_amount / panel.pot.goal_amount) * 100) : 0}% complete
            </p>
          </div>
          <div className="rounded-2xl bg-brand-surface border border-white/5 p-6">
            <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Founders (pending)</div>
            <p className="text-3xl font-semibold text-white">{(pendingFounders || []).length}</p>
            <p className="text-xs text-emerald-400 mt-1">Awaiting approval</p>
          </div>
          <div className="rounded-2xl bg-brand-surface border border-white/5 p-6">
            <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Videos (pending)</div>
            <p className="text-3xl font-semibold text-white">{((pendingVideos || []).length + (pendingLibraryVideos || []).length)}</p>
            <p className="text-xs text-amber-400 mt-1">Needs review</p>
          </div>
        </div>
      </div>

      <div className="bg-brand-surface border-b border-white/5 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-playfair font-bold text-brand-gold" data-testid="admin-title">Admin Panel</h1>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <AdminTabNav
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              badges={{
                pendingFoundersCount: (pendingFounders || []).length,
                pendingVideosCount: (pendingVideos || []).length + (pendingLibraryVideos || []).length,
              }}
            />
          </Tabs>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ReconciliationInbox
          getAuthHeader={getAuthHeader}
          onJumpToTab={(tab) => setActiveTab(tab)}
        />
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">

          {/* Featured Members Tab */}
          <TabsContent value="featured" className="space-y-4">
            <FeaturedMembersTab {...panel} />
          </TabsContent>

          {/* Tier Features Edit Tab */}
          <TabsContent value="tiers" className="space-y-4">
            <TierFeaturesTab {...panel} />
          </TabsContent>

          {/* Tier Access Tab */}
          <TabsContent value="tier-access" className="space-y-6">
            <TierAccessMatrix />
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit-log" className="space-y-6">
            <AuditLog />
          </TabsContent>

          <TabsContent value="compliance" className="space-y-6">
            <CompliancePanel />
          </TabsContent>

          {/* Onboarding — welcome-modal settings + funnel analytics/reset/nudges */}
          <TabsContent value="onboarding" className="space-y-6" data-testid="onboarding-admin-tab">
            <div>
              <h2 className="text-2xl font-playfair font-bold text-foreground">Member Onboarding</h2>
              <p className="text-sm text-muted-foreground mt-1">
                The &quot;Get Started&quot; welcome pop-up new members see on their first Dashboard visit —
                configure the welcome video, watch the funnel, reset or nudge stalled members.
              </p>
            </div>
            <OnboardingSettingsCard getAuthHeader={getAuthHeader} />
            <NudgeEmailCard getAuthHeader={getAuthHeader} />
            <OnboardingAnalyticsCard getAuthHeader={getAuthHeader} />
          </TabsContent>

          {/* Email Delivery Test Tab */}
          <TabsContent value="email-test" className="space-y-6">
            <EmailTestCard getAuthHeader={getAuthHeader} />
          </TabsContent>

          {/* Custom Tiers Tab */}
          <TabsContent value="custom-tiers" className="space-y-6">
            <CustomTiersTab
              customTiers={customTiers}
              showTierForm={showTierForm}
              setShowTierForm={setShowTierForm}
              newTier={newTier}
              setNewTier={setNewTier}
              tierFeaturesInput={tierFeaturesInput}
              setTierFeaturesInput={setTierFeaturesInput}
              handleCreateTier={handleCreateTier}
              handleDeleteTier={handleDeleteTier}
            />
          </TabsContent>

          {/* Video Approval Tab (YouTube + Library) */}
          <TabsContent value="youtube" className="space-y-6">
            <YouTubeApprovalTab
              pendingVideos={pendingVideos}
              pendingLibraryVideos={pendingLibraryVideos}
              handleBatchApproveVideos={handleBatchApproveVideos}
              handleVideoReview={handleVideoReview}
              handleLibraryVideoReview={handleLibraryVideoReview}
            />
          </TabsContent>

          {/* Directory Tab */}
          <TabsContent value="edirectory" className="space-y-4">
            <EdirectoryTab {...panel} />
          </TabsContent>

          {/* Founder Settings Tab */}
          <TabsContent value="founder" className="space-y-4">
            <FounderMembershipTab {...panel} />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <UsersTab {...panel} />
          </TabsContent>

          {/* Sales Tab */}
          <TabsContent value="sales" className="space-y-4">
            <SalesTab {...panel} />
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="space-y-4">
            <SubscriptionsTab getAuthHeader={panel.getAuthHeader} />
          </TabsContent>

          {/* AI Copilot Tab */}
          <TabsContent value="ai-copilot" className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden h-[70vh]">
              <AIAssistant compact={false} showSidebar />
            </div>
          </TabsContent>

          {/* Provider Verifications */}
          <TabsContent value="verifications" className="space-y-4">
            <AdminVerifications />
          </TabsContent>

          {/* Stripe Reconciliation */}
          <TabsContent value="stripe-reconcile" className="space-y-4">
            <StripeReconcileTab getAuthHeader={panel.getAuthHeader} />
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promo" className="space-y-4">
            <PromoTab {...panel} />
          </TabsContent>
          <TabsContent value="pot" className="space-y-4">
            <PotTab {...panel} />
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos" className="space-y-4">
            <VideosTab {...panel} />
          </TabsContent>

          {/* Content Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <AdminReportsTab getAuthHeader={panel.getAuthHeader} />
          </TabsContent>

          {/* Advertisers Tab */}
          <TabsContent value="advertisers" className="space-y-4">
            <AdvertisersTab {...panel} />
          </TabsContent>

          {/* Founder Approvals Tab */}
          <TabsContent value="approvals" className="space-y-4">
            <FounderApprovalsTab {...panel} />
          </TabsContent>

          {/* Downline Management Tab */}
          <TabsContent value="downline" className="space-y-4">
            <DownlineTab {...panel} />
          </TabsContent>

          {/* Commission Dashboard Tab */}
          <TabsContent value="commission-dashboard" className="space-y-4">
            <CommissionDashboardTab {...panel} />
          </TabsContent>

          {/* LMS Tab — links to the real LMS (legacy educational_content retired) */}
          <TabsContent value="content" className="space-y-4">
            <ContentTab {...panel} />
          </TabsContent>

          {/* Scheduler Services Tab */}
          <TabsContent value="scheduler" className="space-y-4">
            <SchedulerAdminTab {...panel} />
          </TabsContent>

          {/* Commissions Tab — multi-level sales matrix editor */}
          <TabsContent value="commissions" className="space-y-6">
            <CommissionMatrixEditor getAuthHeader={getAuthHeader} apiUrl={API_URL} />
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts" className="space-y-6">
            {/* Stripe Connect payouts — members onboard via Stripe; admin sends Transfers */}
            <StripePayoutsPanel getAuthHeader={getAuthHeader} apiUrl={API_URL} />

            {/* Payout History (all methods, incl. legacy PayPal/manual records) */}
            <div className="wm-card p-6 rounded-lg">
              <h3 className="font-semibold text-foreground mb-4">Payout History (all methods)</h3>
              {payoutHistory.length === 0 ? (
                <p className="text-muted-foreground text-sm">No payouts processed yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="py-2 text-muted-foreground">Date</th>
                        <th className="py-2 text-muted-foreground">Member</th>
                        <th className="py-2 text-muted-foreground">Amount</th>
                        <th className="py-2 text-muted-foreground">Method</th>
                        <th className="py-2 text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutHistory.map(p => (
                        <tr key={p.id} className="border-b border-border/30">
                          <td className="py-2 text-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                          <td className="py-2 text-foreground">{p.user_id}</td>
                          <td className="py-2 text-emerald-400 font-medium">${p.amount?.toFixed(2)}</td>
                          <td className="py-2 text-muted-foreground capitalize">{p.payout_type}</td>
                          <td className="py-2">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">{p.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-400 mb-2">How Payouts Work</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>- Members onboard themselves through Stripe in Profile Settings → Payouts (bank, identity and tax info go directly to Stripe).</li>
                <li>- Balances accumulate from credited Directory listing sales and downline commissions.</li>
                <li>- &quot;Preview batch&quot; shows everyone at or above the $25 minimum; &quot;Send&quot; pays them via Stripe Transfers.</li>
                <li>- Funds sit in a 7-day Stripe hold before reaching the member&apos;s bank — reversible during that window if fraud is suspected.</li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="legal" className="space-y-6">
            <LegalAdminEditor />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
